import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { Button, Input } from "@/components/ui";
import { SettlementStepCard, type StepCardProps } from "../SettlementStepCard";

export function RecordCollectionStep({
  busy,
  noSelection,
  submitting,
  collectibleCents,
  onRecord,
  disabled = false,
  ...card
}: StepCardProps & {
  busy: boolean;
  noSelection: boolean;
  submitting: boolean;
  collectibleCents: number;
  /** Rejects when the action failed; the form is then kept for a retry. */
  onRecord: (data: { reference: string; note?: string }) => Promise<void>;
  disabled?: boolean;
}) {
  const { t } = useTranslation("payments");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const formDisabled = disabled || busy || noSelection || collectibleCents <= 0;

  const handleSubmit = async () => {
    if (!reference.trim()) {
      toast.error(t("settlements.detail.referenceRequired"));
      return;
    }
    try {
      await onRecord({ reference: reference.trim(), note: note.trim() || undefined });
      setReference("");
      setNote("");
    } catch {
      /* toast via mutation onError; keep the form so the operator can retry */
    }
  };

  return (
    <SettlementStepCard
      {...card}
      title={t("settlements.detail.steps.collectTitle")}
      description={t("settlements.detail.steps.collectDesc")}
      disabled={formDisabled}
    >
      <div className="space-y-3">
        <Input
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          disabled={formDisabled}
          placeholder={t("settlements.detail.steps.collectReferencePlaceholder")}
        />
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={formDisabled}
          placeholder={t("settlements.detail.collectionNotePlaceholder")}
        />
        <Button
          type="button"
          className="w-full"
          disabled={formDisabled}
          isLoading={submitting}
          onClick={() => void handleSubmit()}
        >
          {t("settlements.detail.steps.collectButton")}
        </Button>
      </div>
    </SettlementStepCard>
  );
}
